import { ErrorMessage, Field, Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { endpoint } from '..';
import Button from '../../../components/Button/Button';
import Map from '../../../components/Map/Map';
import TextArea from '../../../components/TextArea/TextArea';
import { IInitialState } from '../../../reduxService';
import { useAppDispatch, useAppSelector } from '../../../reduxService/hooks';
import { IProjectMacra1 } from '../../MACRA_1/store/project/projectSlice';
import { IProjectMacra2 } from '../../MACRA_2/store/project/projectSlice';
import { getActionByProjectType } from '../../actionMap';
import Layout from '../../components/Layout/Layout';
import { UnitOfMeasure } from '../../enum/project';
import { selectProjectInfo, setProjectInfo } from '../../store/projectInfo/projectInfoSlice';
import { IProjectInfoForm, IProjectInfoProps } from './IProjectInfo';
import './ProjectInfo.scss';
import { selectUser } from '../../store/user/userSlice';

const ProjectInfo = (props: IProjectInfoProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const projectInfo = useAppSelector(selectProjectInfo);
    const user = useAppSelector(selectUser);

    const { selectProject } = getActionByProjectType();
    const project: IInitialState<IProjectMacra1 | IProjectMacra2> = useAppSelector(selectProject as any);

    const requiredMSG = t('project_info_formik_required');
    const ProjectSchema = Yup.object().shape({
        name: Yup.string().required(requiredMSG),
        client: Yup.string().required(requiredMSG),
        company: Yup.string().required(requiredMSG),
        country: Yup.string().required(requiredMSG),
        city: Yup.string().required(requiredMSG),
        latitude: Yup.number().required(requiredMSG),
        longitude: Yup.number().required(requiredMSG),
    });

    const handleInitialValues = () => {
        let initialValue = {
            name: projectInfo.data?.name ?? '',
            author: projectInfo.data?.author ?? '',
            client: projectInfo.data?.client ?? '',
            number: projectInfo.data?.number ?? 0,
            notes: projectInfo.data?.notes ?? '',
            company: projectInfo.data?.company ?? '',
            country: projectInfo.data?.country ?? '',
            city: projectInfo.data?.city ?? '',
            latitude: projectInfo.data?.latitude,
            longitude: projectInfo.data?.longitude,
            unitOfMeasure: UnitOfMeasure.SI,
        };
        return initialValue;
    };

    return (
        <Layout>
            <Formik
                initialValues={handleInitialValues()}
                validationSchema={ProjectSchema}
                enableReinitialize={true}
                onSubmit={values => {
                    const projectInfoToDispatch: IProjectInfoForm = {
                        ...values,
                        author: values.author ? values.author : user.data?.email!,
                        unitOfMeasure: UnitOfMeasure.SI,
                    };
                    if (!project.data || !project.data.geometry) {
                        dispatch(setProjectInfo(projectInfoToDispatch));
                        navigate(endpoint.projectType);
                    } else {
                        dispatch(setProjectInfo({ ...projectInfoToDispatch, type: projectInfo.data?.type, id: projectInfo.data?.id }));
                        navigate(endpoint.designCondition);
                    }
                }}>
                {({ values, setValues, handleChange }) => {
                    return (
                        <Form className='d-flex align-items-center h-100 project-info-form overflow-auto'>
                            <div className='d-flex justify-content-between w-100 px-5 full-height-wrapper'>
                                <div className='w-25'>
                                    <h6>{t('project_info_project_detail_title')}</h6>
                                    <div className='d-flex flex-column gap-1'>
                                        <div className='d-flex flex-column'>
                                            <label>{t('project_info_project_detail_project_name')}</label>
                                            <Field type='text' title={t('project_info_project_detail_project_name')} name='name' placeholder={t('placeholder') as string} />
                                            <ErrorMessage name='name' component='span' className='error-color' />
                                        </div>
                                        <div className='d-flex flex-column'>
                                            <label>{t('project_info_project_detail_client')}</label>
                                            <Field type='text' name='client' placeholder={t('placeholder') as string} />
                                            <ErrorMessage name='client' component='span' className='error-color' />
                                        </div>
                                        <div className='d-flex flex-column'>
                                            <label>{t('project_info_project_detail_project_number')}</label>
                                            <Field type='number' title={t('project_info_project_detail_project_number')} name='number' placeholder={t('placeholder') as string} />
                                        </div>
                                        <div className='d-flex flex-column'>
                                            <label>{t('project_info_location')}</label>
                                            <span>
                                                {t('project_info_project_detail_location_latitude')}: {values.latitude} | {t('project_info_project_detail_location_longitude')}: {values.longitude}
                                            </span>

                                            <ErrorMessage name='longitude' component='span' className='error-color' />
                                        </div>
                                        <TextArea
                                            title={t('project_info_project_detail_notes')}
                                            name='notes'
                                            rows={5}
                                            value={values.notes}
                                            placeholder={t('placeholder') as string}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                                <div className='w-25'>
                                    <h6>{t('project_info_company_detail_title')}</h6>
                                    <div className='gap-1'>
                                        <div className='d-flex flex-column'>
                                            <label>{t('project_info_company_detail_company')}</label>
                                            <Field type='text' title={t('project_info_company_detail_company')} name='company' placeholder={t('placeholder') as string} />
                                            <ErrorMessage name='company' component='span' className='error-color' />
                                        </div>
                                        <div className='d-flex flex-column'>
                                            <label>{t('project_info_company_detail_country')}</label>
                                            <Field type='text' title={t('project_info_company_detail_country')} name='country' placeholder={t('placeholder') as string} />
                                            <ErrorMessage name='country' component='span' className='error-color' />
                                        </div>
                                        <div className='d-flex flex-column'>
                                            <label>{t('project_info_company_detail_city')}</label>
                                            <Field type='text' title={t('project_info_company_detail_city')} name='city' placeholder={t('placeholder') as string} />
                                            <ErrorMessage name='city' component='span' className='error-color' />
                                        </div>
                                    </div>
                                </div>
                                <div className='w-30'>
                                    <h6>{t('project_info_location')}</h6>
                                    <p>
                                        {t('project_info_location_description')}
                                        <br />
                                        {t('project_info_location_retrieve')}
                                    </p>
                                    <Map
                                        onClick={coordinate => {
                                            setValues(stateValues => ({
                                                ...stateValues,
                                                latitude: coordinate.latitude!,
                                                longitude: coordinate.longitude!,
                                            }));
                                        }}
                                        coordinates={{
                                            latitude: values.latitude,
                                            longitude: values.longitude,
                                        }}
                                    />
                                    <Button type='submit' className='d-flex justify-content-end mt-5'>
                                        {!projectInfo.initialState ? t('project_info_go_to_design_condition') : t('next_button')}
                                    </Button>
                                </div>
                            </div>
                        </Form>
                    );
                }}
            </Formik>
        </Layout>
    );
};

export default ProjectInfo;
